// Firm branding: the single definition of the shape, shared by the schema, the
// console editor and the client form.
//
// It used to be hand-duplicated in both apps, which made `isCustomCopy()` in the
// form quietly load-bearing — it compares stored copy against DEFAULT_BRANDING,
// so a comma changed on one side would have started sending French to English
// clients. One type, one default, no drift.

import { v, type Infer } from "convex/values";

export const brandingPresentationValidator = v.object({
  // Greeting headline. `{prenom}` is replaced with the client's first name.
  greeting: v.string(),
  // Intro paragraph. `{cabinet}` is replaced with the firm's display name.
  message: v.string(),
  // Toggle the "Ce dont vous aurez besoin :" checklist.
  showChecklist: v.boolean(),
});

/** What lives on the firm document — the logo is a storage id, not a URL. */
export const storedBrandingValidator = v.object({
  primaryColor: v.string(), // hex, e.g. "#0088FF"
  accentColor: v.string(), // hex — checklist + autosave icons
  backgroundColor: v.string(), // hex — page behind the form card
  cardColor: v.string(), // hex — the form card itself
  logoStorageId: v.optional(v.id("_storage")),
  logoFileName: v.optional(v.string()),
  presentation: brandingPresentationValidator,
  // NOTE: the subdomain is deliberately NOT in here. It's an address, not a
  // color: it has to be unique across firms (so it needs its own index), it
  // must survive "Réinitialiser" (which wipes this whole object), and client
  // links already handed out point at it. It lives at `firms.subdomain`.
});

export type StoredBranding = Infer<typeof storedBrandingValidator>;

/** What the apps render: same fields, logo already resolved to a URL. */
export interface BrandingConfig {
  primaryColor: string;
  accentColor: string;
  backgroundColor: string;
  cardColor: string;
  logoUrl: string | null;
  logoFileName: string | null;
  presentation: {
    greeting: string;
    message: string;
    showChecklist: boolean;
  };
  subdomain: string;
}

// Formio Blue — matches --primary (208 100% 50%) in the form's index.css.
export const DEFAULT_PRIMARY = "#0088FF";
export const DEFAULT_BACKGROUND = "#FFFFFF";
export const DEFAULT_CARD = "#FFFFFF";

export const DEFAULT_BRANDING: BrandingConfig = {
  primaryColor: DEFAULT_PRIMARY,
  accentColor: DEFAULT_PRIMARY,
  backgroundColor: DEFAULT_BACKGROUND,
  cardColor: DEFAULT_CARD,
  logoUrl: null,
  logoFileName: null,
  presentation: {
    greeting: "Bonjour {prenom}",
    message:
      "Vous ouvrez un formulaire de {cabinet}. Un processus simple, étape par étape, pour soumettre votre dossier complet.",
    showChecklist: true,
  },
  subdomain: "",
};

export interface TemplateVars {
  prenom: string;
  /** Family name. Optional in practice: a dossier can be opened before it's known. */
  nom?: string;
  cabinet: string;
}

export function renderTemplate(tpl: string, vars: TemplateVars): string {
  return tpl
    .replace(/\{prenom\}/g, vars.prenom)
    .replace(/\{nom\}/g, vars.nom ?? "")
    .replace(/\{cabinet\}/g, vars.cabinet)
    // A missing family name leaves "Bonjour Jean  ," — collapse what it left behind
    // rather than making the firm write copy that survives an empty token.
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
}

// ── Subdomain ───────────────────────────────────────────────────────────────
// A firm's own form address: `cabinet.formio.ca`. Lives here rather than in the
// console so the editor and the mutation apply the same rules — the reserved
// list used to exist only in the browser, which made it a suggestion.

export const ROOT_DOMAIN = "formio.ca";

/** Hosts that must never resolve to a firm: they're ours, or they're about to be. */
export const RESERVED_SUBDOMAINS = new Set([
  "app",
  "www",
  "api",
  "admin",
  "mail",
  "dashboard",
  "console",
  "staging",
  "dev",
  "test",
  "support",
  "status",
  "docs",
  "blog",
]);

const SUBDOMAIN_RE = /^[a-z0-9-]+$/;

/** Trimmed, lowercased, spaces stripped. `""` means "no custom address". */
export function normalizeSubdomain(raw: string | null | undefined): string {
  return (raw ?? "").trim().toLowerCase().replace(/\s+/g, "");
}

export type SubdomainProblem = "too_short" | "invalid" | "reserved";

/**
 * The i18n suffix of what's wrong (`branding.url_*`), or null if it's fine.
 * An empty subdomain is valid: it means the firm keeps the default address.
 */
export function subdomainError(raw: string | null | undefined): SubdomainProblem | null {
  const sub = normalizeSubdomain(raw);
  if (!sub) return null;
  if (sub.length < 3) return "too_short";
  // Leading/trailing hyphens aren't legal in a DNS label, and `--` is reserved
  // for punycode.
  if (!SUBDOMAIN_RE.test(sub) || sub.startsWith("-") || sub.endsWith("-")) return "invalid";
  if (RESERVED_SUBDOMAINS.has(sub)) return "reserved";
  return null;
}

/** Stored branding (+ its resolved logo URL) → the shape the apps render. */
export function brandingFromStored(
  stored: Partial<StoredBranding> | null | undefined,
  logoUrl: string | null,
  subdomain?: string | null,
): BrandingConfig {
  const presentation = stored?.presentation;
  return {
    primaryColor: stored?.primaryColor || DEFAULT_BRANDING.primaryColor,
    accentColor: stored?.accentColor || DEFAULT_BRANDING.accentColor,
    backgroundColor: stored?.backgroundColor || DEFAULT_BRANDING.backgroundColor,
    cardColor: stored?.cardColor || DEFAULT_BRANDING.cardColor,
    logoUrl,
    logoFileName: stored?.logoFileName ?? null,
    presentation: {
      greeting: presentation?.greeting ?? DEFAULT_BRANDING.presentation.greeting,
      message: presentation?.message ?? DEFAULT_BRANDING.presentation.message,
      showChecklist: presentation?.showChecklist !== false,
    },
    subdomain: subdomain ?? "",
  };
}
